<template>
  <div>
    <v-data-table
      dense
      :headers="headers"
      :loading="loading"
      :items="items"
      item-key="id"
      :options.sync="options"
      :server-items-length="totalItems"
      class="elevation-1"
      :footer-props.sync="footerProps"
      :hide-default-footer="true"
      @update:options="updateTable()"
    >
      <template #body="{ items }">
        <draggable
          :list="items"
          tag="tbody"
          handle=".handle"
          @end="updateOrder"
        >
          <tr
            v-for="(item,index) in items"
            :key="item.id"
          >
            <td>
              <div class="d-flex align-center">
                <span
                  class="handle font-weight-bold mr-2"
                  style="cursor: move;"
                >::</span>
                <v-chip
                  label
                  color="blue"
                  text-color="white"
                  class="mr-2"
                >
                  {{ index + 1 }}
                </v-chip>
              </div>
            </td>
            <td>{{ item.name }}</td>
            <td>{{ item.scoring_model.name }}</td>
            <td>
              <div :class="item.active ? 'badge-green' : 'badge-red'" />
            </td>
            <td>
              <v-icon
                v-if="hasPermission('edit lending-strategies::list')"
                :id="item"
                class="mr-10"
                @click="toggleCard(index)"
              >
                {{ item.showCard ? 'mdi-arrow-up-circle' : 'mdi-arrow-down-circle' }}
              </v-icon>
              <v-card
                v-if="item.showCard"
                class="mt-2"
              >
                <v-card-text>
                  <v-container fluid>
                    <v-form>
                      <v-row>
                        <v-col>
                          <v-simple-table>
                            <thead>
                              <tr>
                                <th>Lead Column</th>
                                <th>Comparison Operators</th>
                                <th>Values</th>
                                <th />
                              </tr>
                            </thead>
                            <tbody>
                              <LendingStrategyConditionRow
                                v-for="(condition, cond_index) in item.formData"
                                :key="`condition_${cond_index}`"
                                :lead-columns="leadColumns"
                                :operators="operators"
                                :condition="condition"
                                :input-defs="inputDefs"
                                :row-error-message="getRowErrorMessages(cond_index)"
                                @remove-row="removeRow(item,cond_index)"
                                @set-condition-value="setConditionValue($event, item, cond_index)"
                              />
                            </tbody>
                            <tfoot>
                              <tr>
                                <td
                                  colspan="4"
                                  class="text-right"
                                >
                                  <v-btn
                                    color="primary"
                                    @click="addRow(item)"
                                  >
                                    <v-icon>
                                      mdi-plus
                                    </v-icon>
                                  </v-btn>
                                </td>
                              </tr>
                            </tfoot>
                          </v-simple-table>
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />

                  <v-btn
                    color="blue darken-1"
                    text
                    :disabled="item.formProcessing"
                    @click="closeModal(index)"
                  >
                    Cancel
                  </v-btn>

                  <v-btn
                    color="blue darken-1"
                    text
                    :disabled="item.formProcessing"
                    @click="updateRules(item)"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </td>
          </tr>
        </draggable>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import dataTableMixin from '@/mixins/dataTableMixin';

import LendingStrategyConditionRow from '@/views/LendingStrategies/LendingStrategyConditionRow.vue';
import draggable from 'vuedraggable';

export default {
  name: 'LendingStrategiesIndex',
  components: {
    LendingStrategyConditionRow,
    draggable,
  },
  mixins: [
    dataTableMixin,
  ],
  data: () => ({
    headers: [
      { text: 'Order', value: 'route_order', sortable: false },
      { text: 'Name', value: 'name', sortable: false },
      { text: 'Scoring Model', value: 'scoring_model.name', sortable: false },
      { text: 'Status', value: 'active', sortable: false },
      { text: 'Rules', value: 'actions', sortable: false },
    ],
    leadColumns: [],
    operators: [],
    inputDefs: {},
    rowErrorMessages: {},
  }),
  methods: {
    ...mapActions('notification', ['setNotification']),
    updateTable() {
      this.loading = true;
      return this.$axios
        .get('api/v1/lending-strategy', {
          params: {
            perPage: 100,
            page: this.options.page,
            sortBy: ['lending_strategies.route_order'],
            sortDesc: [false],
            search: this.search,
          },
        })
        .then((res) => {
          this.loading = false;
          this.items = res.data.data
            .filter((item) => item.active && !item.ab_test_id)
            .map((model) => ({
              ...model,
              showCard: false,
              formValid: false,
              formProcessing: false,
              formData: model.conditions ? JSON.parse(model.conditions) : [{
                lead_column: '',
                operator: '==',
                value: '',
              }],
            })).sort((a, b) => a.route_order - b.route_order);
          this.totalItems = res.data.total;
          this.leadColumns = Object.values(res.data.available_lead_columns);
          this.operators = Object.entries(res.data.operators).map((operator) => ({
            value: operator[0],
            text: operator[1],
          }));

          this.inputDefs = { ...res.data.input_defs };
        });
    },
    async updateRules(item) {
      /* eslint-disable no-param-reassign */
      item.formProcessing = true;
      /* eslint-enable no-param-reassign */

      const data = {
        name: item.name,
        scoring_mo_id: item.scoring_mo_id,
        prod_asgmt_id: item.prod_asgmt_id,
        conditions: item.formData,
      };
      try {
        const result = await this.$axios.put(`/api/v1/lending-strategy/${item.id}`, data);
        if (result.status === 200) {
          this.setNotification({
            message: 'Lending strategy has been saved.',
            color: 'green',
          });
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          const errorData = error.response.data.errors;

          if (errorData) {
            // let errorMessage = 'Please fix the errors';

            const errorKeys = Object.keys(errorData);

            errorKeys.forEach((key) => {
              const keyParts = key.split('.');

              /* if (key === 'isLocked') {
                    // eslint-disable-next-line prefer-destructuring
                    //errorMessage = errorData[key][0];
                  } */

              if (keyParts[0] === 'conditions') {
                if (this.rowErrorMessages[keyParts[1]]) {
                  this.rowErrorMessages[keyParts[1]][keyParts[2]] = errorData[key].pop();
                } else {
                  this.rowErrorMessages[keyParts[1]] = {
                    [keyParts[2]]: errorData[key].pop(),
                  };
                }
              }
            });
          }
        }
      } finally {
        /* eslint-disable no-param-reassign */
        item.showCard = false;
        item.formProcessing = false;
        /* eslint-enable no-param-reassign */
      }
    },
    toggleCard(index) {
      this.items[index].showCard = !this.items[index].showCard;
    },
    closeModal(index) {
      this.items[index].showCard = false;
    },
    addRow(item) {
      item.formData.push({
        lead_column: 'accountholder',
        operator: '==',
        value: '',
      });
    },
    removeRow(item, index) {
      item.formData.splice(index, 1);
    },
    setConditionValue(event, item, index) {
      /* eslint-disable no-param-reassign */
      item.formData[index].value = event;
      /* eslint-enable no-param-reassign */
    },
    getRowErrorMessages(index) {
      if (this.rowErrorMessages[index]) {
        return this.rowErrorMessages[index].value;
      }

      return '';
    },
    async updateOrder(event) {
      const movedItem = this.items.splice(event.oldIndex, 1)[0];
      this.items.splice(event.newIndex, 0, movedItem);
      this.loading = true;
      const updatedOrder = this.items.map((item, index) => ({
        id: item.id,
        route_order: index,
      }));
      try {
        const result = await this.$axios.post('/api/v1/lending-strategy/change-route-order', {
          route_order: updatedOrder,
        });
        if (result.status === 200) {
          this.setNotification({
            message: 'Lending strategy order has been updated.',
            color: 'green',
          });
        }
      } catch (error) {
        this.setNotification({
          message: error.message,
          color: 'red',
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style>
.handle {
  cursor: move;
}

.badge-green {
  width: 20px;
  height: 20px;
  background-color: green;
  border-radius: 50%;
}

.badge-red {
  width: 20px;
  height: 20px;
  background-color: red;
  border-radius: 50%;
}
</style>
